import { hooks } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useRouter } from "next/router";
import { useFormatter, useNow, useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import Button from "~/components/common/button";
import { useConfiguration, useObjectConfigurationValue } from "~/contexts/configuration";
import Logger from "~/services/logger/logger";
import appRoutes from "~/utils/app-routes";

import styles from "./cancel-order.module.scss";
import CancelOrderDialog from "./cancel-order-dialog";

type Props = {
  order: EVA.Core.OrderDto;
};

export default function CancelOrder({ order }: Props) {
  const t = useTranslations();
  const router = useRouter();
  const formatter = useFormatter();
  const { configuration } = useConfiguration();
  const openCancel = router.query.openCancel == "true";
  const [openCancelModal, setOpenCancelModal] = useState(openCancel ?? false);
  const [successfullyCancelled, setSuccessfullyCancelled] = useState(false);
  const [pendingCancellation, setPendingCancellation] = useState(false);

  const cancelOrder = hooks.useCallService({ service: Core.CancelOrder });

  const exportInMinutes = configuration["Orders:DelayMinutes"] ?? 10;
  // const exportInMinutes = useObjectConfigurationValue("App:Ecommerce:ShippingConfigs")?.exportInMinutes ?? 10;

  const now = useNow({
    updateInterval: 1000,
  });

  const cancelableUntil = useMemo(() => {
    if (order?.PlacementDate) {
      const maxCancelDate = new Date(order.PlacementDate);
      maxCancelDate.setMinutes(maxCancelDate.getMinutes() + exportInMinutes);
      return maxCancelDate;
    }
    return new Date();
  }, [order, exportInMinutes]);

  const canBeCancelled = useMemo(() => {
    const notCancelledProducts = order.Lines.filter((line) => line.ProductID && !line.IsCancelled);
    if (
      notCancelledProducts.length > 0 &&
      notCancelledProducts.every((line) => line.ExportStatusID <= 2) &&
      cancelableUntil &&
      now < cancelableUntil
    ) {
      return true;
    } else {
      return false;
    }
  }, [order.Lines, cancelableUntil, now]);

  if (!canBeCancelled) {
    return null;
  }

  return (
    <>
      <div className={styles.cancelBanner}>
        <p>
          {t("generic.cancelorderuntil", {
            time: formatter.relativeTime(cancelableUntil, now),
          })}
        </p>
        <Button lookAsLink onClick={() => setOpenCancelModal(true)}>
          {t("generic.cancelorder")}
        </Button>
      </div>
      <CancelOrderDialog
        isOpen={openCancelModal}
        isPaid={order.IsPaid}
        onClose={() => {
          if (successfullyCancelled) {
            router.push(appRoutes.HOMEPAGE);
          } else {
            setOpenCancelModal(false);
          }
        }}
        onSubmit={async () => {
          setPendingCancellation(true);
          const filteredCancellableOrderLines = order.Lines.filter((line) => line.Type === 0).map((line) => ({
            OrderLineID: line.ID,
          }));
          const cancelResponse = await cancelOrder({
            OrderID: order?.ID,
            OrderLines: filteredCancellableOrderLines,
          });
          Logger.instance.info("cancelResponse: ", cancelResponse);
          // EVA.Core.OrderCancellationOptions.CanBeCancelled
          if (cancelResponse?.Result == 0) {
            setSuccessfullyCancelled(true);
          }
          setPendingCancellation(false);
        }}
        isLoading={pendingCancellation}
        successfullyCancelled={successfullyCancelled}
        timeoutCancel={!canBeCancelled}
      />
    </>
  );
}
